import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@gokrew/models/constants/query-keys';
import type { ReactChildren } from '@gokrew/models/ui.types';
import type { EmployeePermissionScope } from '@gokrew/models/employee-permissions';
import { getEmployeeScopes } from '@/api/permissions';
import { useEmployeePortalAuth } from './EmployeeAuthContext';

/**
 * The type for the PermissionsContext.
 */
type Permissions = Set<Partial<EmployeePermissionScope>>;

/**
 * The type for the PermissionsContext.
 */
interface PermissionsContextType {
  permissions: Permissions;
  fetchingPermissions: boolean;
}

/**
 * The PermissionsContext provides the following properties:
 */
const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

/**
 * A context provider that provides the user's permissions to its children.
 * The component fetches the user's permissions when the user and companyId are available.
 * The permissions are stored in the state and updated when the user or companyId changes.
 *
 * @example
 * <PermissionsProvider>
 *   <MyComponent />
 * </PermissionsProvider>
 *
 * @param children - The children components of the provider.
 * @returns The provider component with the children components.
 */
export function PermissionsProvider({ children }: ReactChildren) {
  const { token } = useEmployeePortalAuth();

  const { data, isLoading } = useQuery({
    queryKey: [QueryKeys.Permissions],
    queryFn: async () => {
      const { data } = await getEmployeeScopes();
      return data.scopes;
    },
    enabled: !!token,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const permissions = useMemo(() => new Set(data || []), [data]);

  return (
    <PermissionsContext.Provider
      value={{ permissions, fetchingPermissions: isLoading }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * A hook that returns the PermissionsContext.
 * The PermissionsContext provides the following properties:
 * - permissions: A set of the user's permissions.
 *
 * The usePermissions hook can only be used within a PermissionsProvider.
 *
 * @throws If the hook is used outside of a PermissionsProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
